import { Profiler, ProfilerOnRenderCallback, type ReactNode } from 'react';
import { config } from '../config/env';

interface PerformanceProfilerProps {
  id: string;
  children: ReactNode;
  onRender?: ProfilerOnRenderCallback;
  enabled?: boolean;
}

/**
 * Wrapper component for React Profiler that logs performance metrics in development.
 * Can be enabled/disabled via props or environment variable.
 */
export function PerformanceProfiler({
  id,
  children,
  onRender,
  enabled = config.NODE_ENV === 'development',
}: PerformanceProfilerProps) {
  const handleRender: ProfilerOnRenderCallback = (
    id: string,
    phase: 'mount' | 'update' | 'nested-update',
    actualDuration: number,
    baseDuration: number,
    startTime: number,
    commitTime: number
  ) => {
    // Custom onRender callback if provided
    if (onRender) {
      onRender(id, phase, actualDuration, baseDuration, startTime, commitTime);
    }

    // Default logging in development
    if (enabled) {
      const performanceData = {
        component: id,
        phase,
        actualDuration: `${actualDuration.toFixed(2)}ms`,
        baseDuration: `${baseDuration.toFixed(2)}ms`,
        startTime: `${startTime.toFixed(2)}ms`,
        commitTime: `${commitTime.toFixed(2)}ms`,
        interactions: 0,
        slowRender: actualDuration > 16, // Flag if render took longer than one frame (16ms at 60fps)
        slowCommit: commitTime - startTime > 16, // Flag if commit took longer than one frame
      };

      if (performanceData.slowRender || performanceData.slowCommit) {
        console.warn(
          `[Performance Profiler] Slow render detected:`,
          performanceData
        );
      }
    }
  };

  if (!enabled) {
    return <>{children}</>;
  }

  return (
    <Profiler id={id} onRender={handleRender}>
      {children}
    </Profiler>
  );
}
